import { dom, utils } from '@omegagrid/core';
import rp, { ParsedComponentId, Dashboard, CodeEditor, CreateComponentOptions, helpers } from '@reporting/rp-app-host';
import { Sidebar } from './components';
import { ReportContainer } from './components/reportContainer';
import { ContextCodeEditorAdapter, ContextFormAdapter } from './adapters';
import { AnalyticsApplication, ContextList } from '.';
import { ModifyContextParams } from './types';
import { cloneContext, isItemUid, isMultiContext } from './common';
import { ReportStore } from './model';
import { FormContainer } from '@omegagrid/form';

const CONFETTI = false;

const app = () => rp.applications.get('analytics') as AnalyticsApplication;
const getContextBroker = () => app().contextBroker;

const createMainSidebar = (componentId: ParsedComponentId) => {
	return dom.createElement<Sidebar>('rp-mf-analytics-sidebar');
}

const createReport = async (componentId: ParsedComponentId, options: Partial<ReportContainer>) => {
	let idContext: number, itemIndex: number, itemUid: string, params: string;

	if (componentId.ids.length > 1) {
		for (let i = 1; i < componentId.ids.length; i++) {
			if (isItemUid(componentId.ids[i])) {
				// stable dashboard item id (dashboard item components)
				itemUid = componentId.ids[i];
			} else if (/[a-b]/i.test(componentId.ids[i])) {
				params = componentId.ids[i];
			} else if (idContext == null) {
				idContext = parseInt(componentId.ids[i]);
			} else {
				// numeric item index (deep links)
				itemIndex = parseInt(componentId.ids[i]);
			}
		}
	}

	let reportContext = cloneContext(idContext
		? await getContextBroker().getContext({id_context: idContext})
		: options.reportContext
	);

	if (params) {
		const modifyParams = helpers.hexDecode<ModifyContextParams>(params);
		reportContext = await getContextBroker().getContext(getContextBroker().modifyContext(reportContext, modifyParams));
	}

	// every report with an id lives in a store. Dashboards render directly
	// from the immutable store draft (single live context object for the
	// container, the adapter, the editor and the sidebar filter). Single
	// reports get a mutable working copy of the draft - visualizations and
	// editors mutate it in place and the container commits it back into the
	// store on edit events.
	let store: ReportStore = null;
	if (isMultiContext(reportContext)) {
		store = app().getOrCreateReportStore(reportContext);
		if (store) reportContext = store.context;
	} else if (reportContext?.id_context) {
		store = app().getOrCreateReportStore(reportContext);
		if (store) reportContext = cloneContext(store.context);
	}

	return dom.createElement<ReportContainer>('rp-report-container', {
		itemIndex: itemIndex,
		itemUid: itemUid,
		reportContext: reportContext,
		store: store,
		...options
	});
}

const createContextCodeEditor = (componentId: ParsedComponentId) => {	
	return dom.createElement<CodeEditor>('rp-editor-code', {
		adapter: new ContextCodeEditorAdapter(parseInt(componentId.ids[1]))
	});
}

const tryRefreshContextBrowser = () => {
	const sidebar = rp.host.components.get('analytics') as Sidebar;
	if (!sidebar) return;
	if (!(sidebar.innerComponent instanceof ContextList)) return;
	sidebar.innerComponent?.browser?.refresh();
};

const createContextForm = async (componentId: ParsedComponentId) =>
	dom.createElement<FormContainer>('og-form-container', {
		adapter: new ContextFormAdapter(await getContextBroker().getContext({id_context: parseInt(componentId.ids[1])})),
		listeners: [['form.save', () => tryRefreshContextBrowser()]]
	});

/** top-level container of any context type (report, dashboard, spreadsheet) */
const createReportContainer = (componentId: ParsedComponentId, options?: CreateComponentOptions) =>
	createReport(componentId, Object.assign({confetti: CONFETTI, toolbar: true}, options?.data));

export const componentFactories = new Map<string, (componentId: ParsedComponentId, options?: CreateComponentOptions) => HTMLElement|Promise<HTMLElement>>([
	['analytics', createMainSidebar],
	['reportContainer', createReportContainer],
	// legacy alias - dashboards used to get their own component id (the only
	// difference was a disabled confetti option). Kept so tabs persisted in
	// the host state still open; new ids are always reportContainer
	['dashboardContainer', createReportContainer],
	['homeReportContainer', (componentId, options) => createReport(componentId, Object.assign({confetti: CONFETTI, toolbar: false}, options?.data))],
	['dashboardItem', (componentId, options) => createReport(componentId, Object.assign({confetti: CONFETTI, toolbar: false}, options?.data))],
	['contextDataEditor', createContextCodeEditor],
	['contextForm', createContextForm]
]);
